import React, { useState } from 'react';
import styles from './SecurityCardDashboard.module.scss';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { FaInfoCircle } from 'react-icons/fa';
import CustomModal from '../../../../../../../../../app/common-components/CustomModal';

/* ── RuleBadge ── */
function RuleBadge({ rule }) {
    return <span className={styles.ruleBadge}>{rule}</span>;
}

/* ── AuditFileRow ── */
function AuditFileRow({ fileName, rules }) {
    return (
        <div className={styles.auditFileRow}>
            <span className={styles.auditFileName} title={fileName}>{fileName}</span>
            <div className={styles.badgeGroup}>
                {rules && rules.length > 0
                    ? rules.map((rule) => <RuleBadge key={rule} rule={rule} />)
                    : <span className={styles.noBadge}>—</span>}
            </div>
        </div>
    );
}

/* ── SecurityCard ── */
function SecurityCard({ card, onEdit, onDelete, onShowMore, onInfo }) {
    if (!card) return null;
    const auditFiles = card.auditFiles || [];
    const auditFileNames = auditFiles.map(f => f.fileName);
    const fileRules = auditFiles.reduce((acc, f) => {
        acc[f.fileName] = f.rulesToExclude || [];
        return acc;
    }, {});

    const MAX_FILES = 2;
    const visibleFiles = auditFileNames.slice(0, MAX_FILES);
    const hiddenCount = auditFileNames.length - MAX_FILES;

    return (
        <div className={styles.card}>
            {/* Header */}
            <div className={styles.cardHeader}>
                <div className={styles.headerLeft}>
                    <span className={styles.cardBadge}>{card.controlType}</span>
                </div>
                <div className={styles.iconGroup}>
                    <button className={styles.iconBtn} onClick={() => onEdit?.(card)} title="Edit">
                        <FiEdit className={styles.cardIcon} />
                    </button>
                    <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => onDelete?.(card)} title="Delete">
                        <FiTrash2 className={styles.cardIcon} />
                    </button>
                    <button className={styles.iconBtn} onClick={() => onInfo?.(card)} title="Info">
                        <FaInfoCircle className={styles.cardIcon} />
                    </button>
                </div>
            </div>

            {/* Total Audit Files */}
            <div className={styles.totalLabel}>
                Total Audit Files:&nbsp;<strong>{auditFileNames.length}</strong>
                {auditFileNames.length > 0 && (
                    <span className={styles.excludedRulesLabel}>Excluded Rules</span>
                )}
            </div>

            {/* Audit File Rows */}
            <div className={styles.fileList}>
                {visibleFiles.length === 0 ? (
                    <p className={styles.emptyMsg}>No audit files assigned</p>
                ) : (
                    visibleFiles.map((file) => (
                        <AuditFileRow
                            key={file}
                            fileName={file}
                            rules={fileRules[file] || []}
                        />
                    ))
                )}
            </div>

            {/* View more */}
            {hiddenCount > 0 && (
                <div className={styles.cardFooter}>
                    <button className={styles.viewMore} onClick={() => onShowMore?.(card)}>
                        View More
                    </button>
                </div>
            )}
        </div>
    );
}


/* ── SecurityCardDashboard ── */
export default function SecurityCardDashboard({
    data = [],
    totalCount = 0,
    loading = false,
    pageNo = 0,
    pageLimit = 10,
    onPageNoChange,
    onPageLimitChange,
    onEdit,
    onDelete,
    onShowMore,
    onInfo,
}) {
    const [auditPopup, setAuditPopup] = useState(null);

    if (!Array.isArray(data) || data.length === 0) {
        return <p className={styles.emptyDashboard}>No Data Available</p>;
    }

    const popupAuditFiles = auditPopup?.auditFiles || [];
    const popupFileNames  = popupAuditFiles.map(f => f.fileName).filter(Boolean);
    const popupFileRules  = popupAuditFiles.reduce((acc, f) => {
        if (f.fileName) acc[f.fileName] = f.rulesToExclude || [];
        return acc;
    }, {});

    const viewMoreBody = () => (
        <>
            <h3 className={styles.modalTitle}>{auditPopup?.controlType || "-"}</h3>
            <div className={styles.modalTable}>
                <div className={styles.tableHeader}>
                    <span>Total Audit Files: <strong>{popupFileNames.length}</strong></span>
                    {popupFileNames.length > 0 && (
                        <span className={styles.popupExcludedLabel}>Excluded Rules</span>
                    )}
                </div>
                {popupFileNames.length === 0 ? (
                    <p className={styles.emptyMsg}>No audit files assigned</p>
                ) : (
                    popupFileNames.map((file) => (
                        <div key={file} className={styles.tableRow}>
                            <span className={styles.fileName}>{file}</span>
                            <div className={styles.badgeGroup}>
                                {(popupFileRules[file] || []).length > 0
                                    ? (popupFileRules[file] || []).map((rule) => (
                                        <span key={rule} className={styles.ruleBadge}>{rule}</span>
                                    ))
                                    : <span className={styles.noBadge}>—</span>
                                }
                            </div>
                        </div>
                    ))
                )}
            </div>
        </>
    );

    return (
        <>
            <div className={styles.grid}>
                {data.map((card) => (
                    <SecurityCard
                        key={card.id ?? card.controlType}
                        card={card}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onShowMore={(card) => setAuditPopup(card)}
                        onInfo={onInfo}
                    />
                ))}
            </div>

            {/* View More Modal */}
            <CustomModal
                modalOpen={!!auditPopup}
                modalHide={() => setAuditPopup(null)}
                closeBtn={true}
                closeBtnFuc={() => setAuditPopup(null)}
                modalBody={viewMoreBody}
                size="md"
                modalCustomClass={styles.viewMoreModal}
            />
        </>
    );
}
